import { SettingOutlined } from "@ant-design/icons";
import { Button, Form, FormInstance, Modal, Select } from "antd";
import { SelectValue } from "antd/lib/select";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useConfigureSCBOne,
  useSCBOneConfiguration,
} from "../../api/api.scb-one";
import { ConfigFormSingle } from "./ConfigFormSingle";

interface SCBOneConfigModalSingleProps {
  selectedSerial: string;
}
export const SCBOneConfigModalSingle = ({
  selectedSerial,
}: SCBOneConfigModalSingleProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavedDisabled, setIsSavedDisabled] = useState(true);
  const [profiles, setProfiles] = useState<Record<string, string | number>[]>(
    []
  );

  const success = () => {
    Modal.success({
      title: t("beacon:configure-success-title"),
      content: t("beacon:configure-success-body"),
      onOk() {
        setIsOpen(false);
      },
    });
  };

  const error = () => {
    Modal.error({
      title: t("error"),
      content: t("beacon:configure-failed-title"),
    });
  };

  const setFormDataReady = (ready: boolean) => {
    setIsSavedDisabled(!ready);
  };

  const configure = useConfigureSCBOne();

  const saveConfiguration = async () => {
    const values = form.getFieldsValue();
    const desiredConfig = {
      positioning: {
        heartbeat: Number(values["positioning.heartbeat"]),
        ttffTimeout: Number(values["positioning.ttffTimeout"]),
        ttffRetries: Number(values["positioning.ttffRetries"]),
        fixQualityTimeout: Number(values["positioning.fixQualityTimeout"]),
        dynamicModel: Number(values["positioning.dynamicModel"]),
      },
      rtk: {
        protocol: "ntrip-v2",
        credentials: {
          host: values["rtk.credentials.host"],
          port: Number(values["rtk.credentials.port"]),
          mountpoint: values["rtk.credentials.mountpoint"],
          user: values["rtk.credentials.user"],
          password: values["rtk.credentials.password"],
        },
      },
      continuousPositioning: {
        refreshSchedule: Number(
          values["continuousPositioning.refreshSchedule"]
        ),
        refreshTimeout: Number(values["continuousPositioning.refreshTimeout"]),
        maxSamples: Number(values["continuousPositioning.maxSamples"]),
      },
    };

    setIsSaving(true);

    const result = await configure({
      serial: selectedSerial,
      configuration: desiredConfig,
    });
    setIsSaving(false);

    if (result.error) error();
    else success();
  };

  const resetModal = () => {
    form.resetFields();
    setIsSavedDisabled(false);
    setIsOpen(false);
  };

  const onProfileChange = (value: SelectValue) => {
    const profile = profiles.find(
      (p: Record<string, string | number>) => p.name === value
    );
    if (!profile) {
      return;
    }
    form.setFieldsValue(profile.configuration);
  };

  return (
    <span data-testid="config-modal">
      <SettingOutlined
        onClick={() => {
          setIsOpen(true);
        }}
        title="Configure"
      />
      <Modal
        width="50%"
        style={{ top: Math.max(16, window.innerHeight * 0.15 - 77) }}
        bodyStyle={{
          overflowY: "auto",
        }}
        title={t("beacon:configuration-title", {
          type: "SCB-One",
          serial_or_id: selectedSerial,
        })}
        open={isOpen}
        onCancel={() => resetModal()}
        onOk={saveConfiguration}
        destroyOnClose
        confirmLoading={isSaving}
        footer={[
          <ConfigurationFooter
            key="footer"
            onProfileChange={onProfileChange}
            saveConfiguration={saveConfiguration}
            resetModal={resetModal}
            isSavedDisabled={isSavedDisabled}
            isSaving={isSaving}
            profiles={profiles}
            showConfigSelect={false}
          />,
        ]}
      >
        <FormWrapper
          form={form}
          setFormDataReady={setFormDataReady}
          selectedBeaconSerial={selectedSerial}
        />
      </Modal>
    </span>
  );
};

/*
  Ant Desing modal is rendered on load of page, so we need to use a wrapper
  component to fetch the configuration and pass it to the form to prevent the 
  hook useBeaconConfiguration from being called before the modal is opened.

  The hook call was used in the form component, but with addition of SCB-one
  configuration, the form needs to have passed the configuration as a prop, so 
  the form can be reused for both beacons and SCB-one devices.
*/
interface FormWrapperProps {
  selectedBeaconSerial: string;
  form: FormInstance<any>;
  setFormDataReady: (ready: boolean) => void;
}

function FormWrapper({
  selectedBeaconSerial,
  form,
  setFormDataReady,
}: FormWrapperProps) {
  const configuration = useSCBOneConfiguration(selectedBeaconSerial);
  return (
    <ConfigFormSingle
      context="scb-one"
      configuration={configuration}
      form={form}
      setFormDataReady={setFormDataReady}
    />
  );
}

interface ConfigurationFooterProps {
  onProfileChange: (value: SelectValue) => void;
  saveConfiguration: () => void;
  resetModal: () => void;
  isSavedDisabled: boolean;
  isSaving: boolean;
  profiles: Record<string, string | number>[];
  showConfigSelect: boolean;
}

export function ConfigurationFooter({
  onProfileChange,
  saveConfiguration,
  resetModal,
  isSavedDisabled,
  isSaving,
  profiles,
  showConfigSelect,
}: ConfigurationFooterProps) {
  const { t } = useTranslation();
  return (
    <div
      key="footer-container"
      style={{
        display: "flex",
        justifyContent: "right",
        width: "100%",
      }}
    >
      {showConfigSelect && (
        <Select
          key="profile-select"
          style={{ flexGrow: 1, maxWidth: "225px" }}
          placeholder={
            <div style={{ color: "black", textAlign: "left" }}>
              Load configuration profile
            </div>
          }
          onChange={(value) => onProfileChange(value)}
        >
          {profiles.map(
            (profile: Record<string, string | number>, index: number) => (
              <Select.Option key={profile.name || index} value={profile.name}>
                {profile.name}
              </Select.Option>
            )
          )}
        </Select>
      )}
      <div key="button-group" style={{ display: "flex" }}>
        <Button key="cancel-button" onClick={() => resetModal()}>
          {t("translations:cancel")}
        </Button>
        <Button
          key="save-button"
          type="primary"
          onClick={saveConfiguration}
          disabled={isSavedDisabled}
          loading={isSaving}
        >
          {t("translations:save")}
        </Button>
      </div>
    </div>
  );
}
